import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextInputKeyPressEvent } from 'react-native';

import { DoubleArrow } from '@/assets/icons/DoubleArrow';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { getPlayerNameInfo } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { QuickButton } from '@/feature/quick-amounts/hooks/use-user-quick-amounts-store';
import { useCurrencyNumberPad } from '@/hooks/use-currency-number-pad';
import { useWalletStore } from '@/hooks/use-wallet';
import { Currency, GameMode } from '@/types/api.generated';
import { toLocaleCurrency } from '@/utils/numeric/currency';
import debounce from 'lodash.debounce';

import { useBetslipData } from '../../hooks/use-betslip-data';
import { useBetslipStore } from '../../hooks/use-betslip-store';
import { useMysteryMode } from '../../hooks/use-mystery-mode';
import {
    getDefaultEntryAmount,
    getWinningPicksAndToWin,
    hasMultiplierExceededError,
    isFreeEntryPromo,
} from '../../utils/betslip-utils';
import { getGameModeDetails } from '../../utils/game-mode-utils';
import { CheckoutWidget } from '../CheckoutWidget';
import { AmountInput } from './AmountInput';
import { QuickAmountsButtons } from './QuickAmountsButtons';

export const MAX_ALLOWED_ENTRY_AMOUNT = 99999999;

export const AmountInputWidget = ({
    onFocusInput,
    onBlurInput,
}: {
    onFocusInput: () => void;
    onBlurInput: () => void;
}) => {
    const { t } = useTranslation(['betslip_pickem']);

    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const entryAmount = useBetslipStore(state => state.entryAmount);
    const currency = useBetslipStore(state => state.currency);

    const {
        displayValue: textAmount,
        number: inputNumber,
        setInputValue: setTextAmount,
        onNumberPress,
        onDecimalPress,
        onBackspacePress,
    } = useCurrencyNumberPad(entryAmount, 8); // allow max 8 chars ($99999999)

    const inputNumberRef = useRef(inputNumber);
    inputNumberRef.current = inputNumber;
    // initially the quick amount buttons act as 'set' buttons
    // after the user presses any quick amount button, they act as 'add' buttons
    const quickAmountsMode = useBetslipStore(state => state.quickAmountsMode);
    const setQuickAmountsMode = useBetslipStore(state => state.actions.setQuickAmountsMode);

    const betrBucks = useWalletStore(state => state.betrBucks);
    const realMoneyTotal = useWalletStore(state => state.realMoneyTotal);
    const setEntryAmount = useBetslipStore(state => state.actions.setEntryAmount);
    const selectedPromo = useBetslipStore(state => state.selectedPromo);
    const freeEntryApplied = isFreeEntryPromo(selectedPromo);

    const {
        pickslipEntryRules,
        betslip,
        perfectBoostedMultiplier,
        perfectRegularMultiplier,
        dynamicBoostedMultiplier,
        dynamicMultipliers,
        mysteryMultipliers,
    } = useBetslipData();

    const { mysteryApplied, mysteryToWin } = useMysteryMode();

    const { highestToWin, highestMultiplier, lowestMultiplier } = getWinningPicksAndToWin(
        dynamicMultipliers.DYNAMIC,
        dynamicBoostedMultiplier
    );

    const validationData = useBetslipStore(state => state.validationData[activeGameMode]);
    const mysteryValidationData = useBetslipStore(state => state.validationData[GameMode.Mystery]);
    const hidePayoutDetails = hasMultiplierExceededError(validationData.betValidation) || freeEntryApplied;

    const maxEntryAmount = useMemo(() => {
        if (mysteryApplied) {
            return mysteryValidationData.amountValidation.maxAllowedEntryAmount;
        }
        return validationData.amountValidation.maxAllowedEntryAmount;
    }, [
        mysteryApplied,
        mysteryValidationData.amountValidation.maxAllowedEntryAmount,
        validationData.amountValidation.maxAllowedEntryAmount,
    ]);

    const defaultEntryAmount = getDefaultEntryAmount(currency, { betrBucks, realMoneyTotal }, maxEntryAmount);

    useEffect(() => {
        // needed to keep the input in sync with the store entry amount
        // if the values are the same, we skip the update
        if (inputNumberRef.current === entryAmount) {
            return;
        }
        setTextAmount(`${entryAmount || 0}`);
    }, [entryAmount, setTextAmount]);

    const { name } = getGameModeDetails(activeGameMode, pickslipEntryRules);
    const picksGameModeTitle = useMemo(
        () => t('picks_game_mode', { count: betslip.length, gameMode: name }),
        [betslip.length, name, t]
    );

    const multiplier = useMemo(() => {
        if (mysteryApplied && mysteryMultipliers) {
            return `${mysteryMultipliers.min}x - ${mysteryMultipliers.max}x`;
        }

        if (activeGameMode === GameMode.Perfect) {
            const perfectMultiplier = perfectBoostedMultiplier || perfectRegularMultiplier;
            return perfectMultiplier ? `${perfectMultiplier}x` : '';
        }

        return `${lowestMultiplier}x-${highestMultiplier}x`;
    }, [
        activeGameMode,
        highestMultiplier,
        lowestMultiplier,
        mysteryApplied,
        mysteryMultipliers,
        perfectBoostedMultiplier,
        perfectRegularMultiplier,
    ]);

    const toWin = useMemo(() => {
        if (mysteryApplied) {
            return mysteryToWin;
        }
        if (activeGameMode === GameMode.Perfect) {
            return validationData.betValidation?.toWin;
        }
        return highestToWin;
    }, [activeGameMode, highestToWin, mysteryApplied, mysteryToWin, validationData.betValidation?.toWin]);

    const usingBetrBucks = currency === Currency.Fre;

    const playersNames = useMemo(
        () => betslip.map(pick => getPlayerNameInfo(pick.player).mainName).join(', '),
        [betslip]
    );

    const setEntryAmountDebounced = useMemo(
        () =>
            debounce((newEntryAmount: number) => {
                setEntryAmount(newEntryAmount);
            }, 200),
        [setEntryAmount]
    );

    const onAmountChange = useCallback(
        (newAmount: string) => {
            const amount = parseFloat(newAmount);

            if (newAmount === '' || newAmount === null || isNaN(amount) || amount === 0) {
                // prevent automatically setting the default amount after clearing the input
                setEntryAmountDebounced(0);
                if (quickAmountsMode !== 'set') {
                    setQuickAmountsMode('set');
                }

                return;
            }

            if (entryAmount === 0) {
                BetrAnalytics.trackEvent(AnalyticsEvent.INPUT_ENTRY_AMOUNT);
            }

            if (quickAmountsMode !== 'add') {
                setQuickAmountsMode('add');
            }

            setEntryAmountDebounced(amount || 0);
        },
        [entryAmount, quickAmountsMode, setEntryAmountDebounced, setQuickAmountsMode]
    );

    const maxValueWithBalance = useMemo(
        () => (usingBetrBucks ? Math.min(maxEntryAmount, betrBucks) : Math.min(maxEntryAmount, realMoneyTotal)),
        [usingBetrBucks, maxEntryAmount, betrBucks, realMoneyTotal]
    );

    const handlePressQuickAmount = useCallback(
        (quickAmount: QuickButton) => {
            const amount = Number(textAmount);

            let selectedAmount: number | null = null;

            if (quickAmount.id === 'MAX') {
                // if the quick amount is MAX, we set the max allowed value
                selectedAmount = maxValueWithBalance;
            } else if (quickAmount.value) {
                // otherwise, we either set it directly
                // or we add it to the current entry amount
                if (quickAmountsMode === 'set') {
                    selectedAmount = quickAmount.value;
                } else {
                    if (amount === MAX_ALLOWED_ENTRY_AMOUNT || amount + quickAmount.value > MAX_ALLOWED_ENTRY_AMOUNT) {
                        return;
                    } else {
                        selectedAmount = amount + quickAmount.value;
                    }
                }
            }

            if (selectedAmount) {
                setTextAmount(`${selectedAmount}`);
                if (quickAmountsMode === 'set') {
                    setQuickAmountsMode('add');
                }
                setEntryAmountDebounced(selectedAmount || 0);
            }
        },
        [textAmount, maxValueWithBalance, quickAmountsMode, setTextAmount, setEntryAmountDebounced, setQuickAmountsMode]
    );

    const onClearPress = useCallback(() => {
        // set text immediately so the useEffect doesn't update the text again
        setTextAmount('0');
        if (entryAmount !== 0) {
            setEntryAmount(0);
        }
        if (quickAmountsMode !== 'set') {
            setQuickAmountsMode('set');
        }
    }, [entryAmount, quickAmountsMode, setEntryAmount, setQuickAmountsMode, setTextAmount]);

    const onKeyPress = useCallback(
        (e: TextInputKeyPressEvent) => {
            let next = '';
            if (e.nativeEvent.key === 'Backspace') {
                next = onBackspacePress();
            } else if (e.nativeEvent.key === ',' || e.nativeEvent.key === '.') {
                next = onDecimalPress();
            } else {
                const parsedNumber = parseInt(e.nativeEvent.key, 10);
                if (!isNaN(parsedNumber)) {
                    next = onNumberPress(parsedNumber);
                }
            }
            onAmountChange(next);
        },
        [onAmountChange, onBackspacePress, onDecimalPress, onNumberPress]
    );

    const validatingBetslip = useBetslipStore(state => state.validating);

    // a free entry is paid by the promo, so balance-related amount errors don't apply
    const showError =
        !freeEntryApplied &&
        !!entryAmount &&
        entryAmount > 0 &&
        !!validationData.amountValidation?.errorMessage &&
        !validatingBetslip &&
        entryAmount === validationData.betValidation?.amount;
    const promotionsAvailable = validationData.betValidation?.promotions;
    const promoApplied = promotionsAvailable?.find(promo => promo.promotion.id === selectedPromo?.promotion.id);

    const placeholder = entryAmount === defaultEntryAmount ? `${defaultEntryAmount}` : undefined;
    const textAmountEmpty = textAmount === '' || textAmount === '0';
    const placeholderEmpty = !placeholder || placeholder === '0';

    const formattedAmount = useMemo(() => {
        if (textAmountEmpty) {
            return '';
        }
        return textAmount;
    }, [textAmount, textAmountEmpty]);

    const editable = !freeEntryApplied;
    const inputLabel = freeEntryApplied
        ? t('free_entry')
        : t('entry', { maxEntryAmount: toLocaleCurrency(maxEntryAmount, true) });

    return (
        <CheckoutWidget>
            <TitleAndMultiplierRow
                title={picksGameModeTitle}
                hidePayoutDetails={hidePayoutDetails}
                multiplier={multiplier}
            />
            <SizedBox value={1} />
            <PlayersNamesRow playersNames={playersNames} />
            <AmountInput
                showError={showError}
                toWin={textAmountEmpty && placeholderEmpty ? undefined : toWin}
                amount={formattedAmount}
                onKeyPress={onKeyPress}
                // if the user focuses the input and the amount is the default amount,
                // we clear the text amount and keep the placeholder
                placeholder={placeholder}
                onFocusInput={() => {
                    if (entryAmount === defaultEntryAmount) {
                        setTextAmount('');
                    }
                    onFocusInput();
                }}
                onEndEditing={e => {
                    if (e.nativeEvent.text === '' && entryAmount === defaultEntryAmount) {
                        // if the user leaves the input empty and the entry amount is the default one,
                        // set back the amount
                        setTextAmount(`${defaultEntryAmount}`);
                    }
                }}
                onClearPress={onClearPress}
                onBlurInput={() => {
                    if (textAmount.endsWith('.')) {
                        setTextAmount(textAmount.slice(0, -1));
                    }
                    onBlurInput();
                }}
                promoApplied={promoApplied}
                shouldHideClearIcon={textAmountEmpty && placeholderEmpty ? true : false}
                alwaysHideClearIcon={freeEntryApplied}
                editable={editable}
                amountInputLabel={inputLabel}
            />
            <QuickAmountsButtons handlePressQuickAmount={handlePressQuickAmount} disabled={freeEntryApplied} />
        </CheckoutWidget>
    );
};

const styles = StyleSheet.create({
    marginTop: {
        marginTop: 1,
    },
});

const PlayersNamesRow = memo(({ playersNames }: { playersNames: string }) => {
    return (
        <Row alignItems={'center'}>
            <Text variant={'bodyMedium'} color={'neutral300'} numberOfLines={1}>
                {playersNames}
            </Text>
        </Row>
    );
});

const TitleAndMultiplierRow = memo(
    ({ title, hidePayoutDetails, multiplier }: { title: string; hidePayoutDetails: boolean; multiplier: string }) => {
        return (
            <Row gap={'s4'} alignItems={'center'}>
                <Text variant={'titleLarge'} letterSpacing={-0.43} color={ds.colorToken.text.primary}>
                    {title}
                </Text>
                {!hidePayoutDetails ? (
                    <Row alignItems={'center'} gap={'s4'}>
                        <Box style={styles.marginTop}>
                            <DoubleArrow />
                        </Box>
                        <Text variant={'titleLarge'} letterSpacing={-0.43} color={ds.colorToken.text.brand}>
                            {multiplier}
                        </Text>
                    </Row>
                ) : null}
            </Row>
        );
    }
);
